import { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { MapPin, Lock, Unlock, ChevronDown, User, Mail, Eye, EyeOff, Crown, Building, Search, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import { TOP_50_CITIES, CITY_TARGET_DATA, STATE_NAMES, generatePlaceholderData } from '../data/targetListData';

// Get unique states from cities
const STATES = [...new Set(TOP_50_CITIES.map(c => c.state))].sort();
const REGIONS = ['All', 'Northeast', 'South', 'Midwest', 'West'];

// Generate Top100 by extending Top10 with placeholders
function buildTop100FromTop10(list10) {
  const base = [...list10];
  for (let i = 11; i <= 100; i++) {
    base.push({ 
      rank: i, 
      name: `Top Account #${i}`, 
      city: "Major city",
      buyer: "Beverage Buyer",
      email: `buyer${i}@account.com`,
      title: i < 30 ? "Wine Director" : i < 60 ? "Bar Manager" : "Category Manager"
    });
  }
  return base;
}

// Table Row Component
function AccountRow({ account, showEmail, isMember }) {
  const [emailVisible, setEmailVisible] = useState(false);

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
      <td className="py-3 px-4 text-center">
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
          account.rank <= 3 ? 'bg-copper/10 text-copper' : 
          account.rank <= 10 ? 'bg-navy/10 text-navy' : 
          'bg-slate-100 text-slate-500'
        }`}>
          {account.rank}
        </span>
      </td>
      <td className="py-3 px-4">
        <div className="font-medium text-navy">{account.name}</div>
        <div className="text-xs text-slate-500">{account.city}</div>
      </td>
      <td className="py-3 px-4 hidden md:table-cell">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">{account.buyer}</span>
        </div>
        <div className="text-xs text-slate-400 ml-6">{account.title}</div>
      </td>
      <td className="py-3 px-4">
        {showEmail ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEmailVisible(!emailVisible)}
              className="text-copper hover:text-copper/80 transition-colors"
              aria-label={emailVisible ? "Hide email" : "Show email"}
            >
              {emailVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            {emailVisible ? (
              <a 
                href={`mailto:${account.email}`} 
                className="text-sm text-copper hover:underline"
              >
                {account.email}
              </a>
            ) : (
              <span className="text-sm text-slate-400">Click to reveal</span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-slate-400">
            <Lock className="w-4 h-4" />
            <span className="text-sm">Members only</span>
          </div>
        )}
      </td>
    </tr>
  );
}

// Account Table Component
function AccountTable({ title, accounts, showEmail, isMember, type }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className={`px-6 py-4 border-b border-slate-200 ${
        type === 'on' ? 'bg-gradient-to-r from-navy/5 to-transparent' : 'bg-gradient-to-r from-copper/5 to-transparent'
      }`}>
        <h3 className="font-heading text-lg font-semibold text-navy flex items-center gap-2">
          <Building className={`w-5 h-5 ${type === 'on' ? 'text-navy' : 'text-copper'}`} />
          {title}
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          {type === 'on' ? 'Restaurants, bars, hotels, clubs' : 'Retail stores, wine shops, liquor stores'}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-3 px-4 text-center w-16">Rank</th>
              <th className="py-3 px-4 text-left">Account</th>
              <th className="py-3 px-4 text-left hidden md:table-cell">Buyer Contact</th>
              <th className="py-3 px-4 text-left">Email</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <AccountRow 
                key={`${type}-${account.rank}`} 
                account={account} 
                showEmail={showEmail}
                isMember={isMember}
              />
            ))}
          </tbody>
        </table>
      </div>
      {!isMember && accounts.length === 10 && (
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-t border-slate-200 text-center">
          <p className="text-sm text-slate-500">
            <Lock className="w-4 h-4 inline mr-1" />
            <strong className="text-navy">90 more accounts</strong> available for members
          </p>
        </div>
      )}
    </div>
  );
}

export default function TargetList() {
  // Apply SEO meta tags for Target List / Buyer database
  useSEO('targetList');
  
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // View mode: 'city' or 'state'
  const [viewMode, setViewMode] = useState('city');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || 'New York');
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || 'NY');
  const [selectedRegion, setSelectedRegion] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Real membership check: authenticated user with active membership
  const demoMode = searchParams.get('tier') === 'member';
  const isMember = (isAuthenticated && user?.membership_status === 'active') || demoMode;

  // Filter cities based on region and search
  const filteredCities = useMemo(() => {
    return TOP_50_CITIES.filter(city => {
      const matchesRegion = selectedRegion === 'All' || city.region === selectedRegion;
      const matchesSearch = searchQuery === '' || 
        city.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.state.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (STATE_NAMES[city.state] || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesRegion && matchesSearch;
    });
  }, [selectedRegion, searchQuery]);

  // Get cities for selected state
  const citiesInState = useMemo(() => {
    return TOP_50_CITIES.filter(c => c.state === selectedState);
  }, [selectedState]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (viewMode === 'city') {
      params.set('city', selectedCity);
      params.delete('state');
    } else {
      params.set('state', selectedState);
      params.delete('city');
    }
    setSearchParams(params, { replace: true });
  }, [viewMode, selectedCity, selectedState, searchParams, setSearchParams]);

  // Get data for selected city
  const data = useMemo(() => {
    if (viewMode === 'city') {
      const cityData = CITY_TARGET_DATA[selectedCity];
      if (cityData) {
        if (isMember) {
          return {
            on: buildTop100FromTop10(cityData.on),
            off: buildTop100FromTop10(cityData.off),
          };
        }
        return cityData;
      }
      // Generate placeholder for cities without detailed data
      const cityInfo = TOP_50_CITIES.find(c => c.city === selectedCity);
      const placeholder = generatePlaceholderData(selectedCity, cityInfo?.state || 'US');
      return isMember ? {
        on: buildTop100FromTop10(placeholder.on),
        off: buildTop100FromTop10(placeholder.off),
      } : placeholder;
    }
    // State view - aggregate from cities in that state
    const stateCities = TOP_50_CITIES.filter(c => c.state === selectedState);
    if (stateCities.length === 0) {
      const placeholder = generatePlaceholderData('Major City', selectedState);
      return isMember ? {
        on: buildTop100FromTop10(placeholder.on),
        off: buildTop100FromTop10(placeholder.off),
      } : placeholder;
    }
    // Use first city's data or generate
    const firstCity = stateCities[0].city;
    const cityData = CITY_TARGET_DATA[firstCity] || generatePlaceholderData(firstCity, selectedState);
    return isMember ? {
      on: buildTop100FromTop10(cityData.on),
      off: buildTop100FromTop10(cityData.off),
    } : cityData;
  }, [viewMode, selectedCity, selectedState, isMember]);

  const handleDemoMember = () => {
    const params = new URLSearchParams(searchParams);
    params.set('tier', 'member');
    setSearchParams(params);
  };

  const handleBackToFree = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('tier');
    setSearchParams(params);
  };

  const showUpgradePrompt = isAuthenticated && user?.membership_status !== 'active' && !demoMode;

  // Current city/state info for display
  const currentCityInfo = TOP_50_CITIES.find(c => c.city === selectedCity);

  return (
    <Layout>
      {/* SEO-Optimized Hero Section */}
      <section className="bg-navy py-16 md:py-20" data-testid="target-list-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-3">
              Beverage Startup Target List
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Top Beverage Accounts in {viewMode === 'city' ? selectedCity : STATE_NAMES[selectedState] || selectedState}
            </h1>
            <p className="text-white/80 text-lg mb-6">
              Launch your spirit, wine, or RTD beverage brand with direct access to qualified buyers. 
              Top 50 US cities covered with on-premise and off-premise beverage buyer contacts.
            </p>
            
            {/* Access Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              {isMember ? (
                <>
                  <Unlock className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-green-300">
                    {demoMode ? 'Demo Mode — Top 100 + Emails' : 'Member Access — Top 100 + Emails'}
                  </span>
                </>
              ) : isAuthenticated ? (
                <>
                  <Crown className="w-4 h-4 text-copper" />
                  <span className="text-sm font-medium text-white/80">Logged in — Upgrade to unlock full list</span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 text-white/60" />
                  <span className="text-sm font-medium text-white/80">Free Preview — Top 10, Emails Locked</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-slate-50 min-h-screen" data-testid="target-list-section">
        <div className="container-custom">
          {/* View Mode Toggle & Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-8">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-white rounded-xl p-1 border border-slate-200 w-fit">
              <button
                onClick={() => setViewMode('city')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'city' 
                    ? 'bg-navy text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="view-by-city-btn"
              >
                <MapPin className="w-4 h-4 inline mr-2" />
                By City
              </button>
              <button
                onClick={() => setViewMode('state')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'state' 
                    ? 'bg-navy text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="view-by-state-btn"
              >
                <Building className="w-4 h-4 inline mr-2" />
                By State
              </button>
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search cities or states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper"
                data-testid="search-input"
              />
            </div>

            {/* Region Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="pl-10 pr-8 py-2.5 rounded-xl border border-slate-200 bg-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper"
                data-testid="region-select"
              >
                {REGIONS.map(region => (
                  <option key={region} value={region}>{region} Region</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* City/State Selection Grid */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {viewMode === 'city' ? `Select City (${filteredCities.length} cities)` : `Select State (${STATES.length} states)`}
            </h2>
            
            {viewMode === 'city' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                {filteredCities.map(city => (
                  <button
                    key={city.city}
                    onClick={() => setSelectedCity(city.city)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                      selectedCity === city.city
                        ? 'bg-copper text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                    data-testid={`city-btn-${city.city.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="truncate">{city.city}</div>
                    <div className={`text-xs ${selectedCity === city.city ? 'text-white/70' : 'text-slate-400'}`}>
                      {city.state}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {STATES.map(state => (
                  <button
                    key={state}
                    onClick={() => setSelectedState(state)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedState === state
                        ? 'bg-copper text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                    data-testid={`state-btn-${state.toLowerCase()}`}
                  >
                    {state} - {STATE_NAMES[state] || state}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Current Selection Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-navy flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-copper" />
                  {viewMode === 'city' ? selectedCity : STATE_NAMES[selectedState] || selectedState}
                  {currentCityInfo && viewMode === 'city' && (
                    <span className="text-sm font-normal text-slate-500">
                      , {STATE_NAMES[currentCityInfo.state] || currentCityInfo.state}
                    </span>
                  )}
                </h2>
                <p className="text-slate-600 mt-1">
                  {viewMode === 'city' && currentCityInfo ? (
                    <>
                      {currentCityInfo.region} region • Tier {currentCityInfo.tier} market •{' '}
                      {CITY_TARGET_DATA[selectedCity] ? 'Detailed buyer contacts available' : 'Placeholder data — full data coming soon'}
                    </>
                  ) : (
                    <>
                      {citiesInState.length} major cities in database
                    </>
                  )}
                </p>
              </div>

              {/* Demo/Member Toggle */}
              <div className="flex items-center gap-3">
                {demoMode ? (
                  <button
                    onClick={handleBackToFree}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                  >
                    Exit Demo
                  </button>
                ) : !isAuthenticated ? (
                  <>
                    <button
                      onClick={handleDemoMember}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-copper/10 text-copper hover:bg-copper/20 transition-colors"
                      data-testid="demo-member-btn"
                    >
                      Preview as Member
                    </button>
                    <Link
                      to="/request-access"
                      className="btn-copper text-sm"
                      data-testid="join-btn"
                    >
                      Join Coalition
                    </Link>
                  </>
                ) : showUpgradePrompt ? (
                  <Link
                    to="/request-access"
                    className="btn-copper text-sm"
                  >
                    Upgrade Membership
                  </Link>
                ) : null}
              </div>
            </div>
          </div>

          {/* Account Tables */}
          <div className="grid lg:grid-cols-2 gap-6">
            <AccountTable
              title="On-Premise Accounts"
              accounts={data.on}
              showEmail={isMember}
              isMember={isMember}
              type="on"
            />
            <AccountTable
              title="Off-Premise Accounts"
              accounts={data.off}
              showEmail={isMember}
              isMember={isMember}
              type="off"
            />
          </div>

          {/* SEO Content Section */}
          <div className="mt-12 bg-white rounded-2xl p-8 border border-slate-200">
            <h2 className="font-heading text-xl font-bold text-navy mb-4">
              Launch Your Beverage Brand in {viewMode === 'city' ? selectedCity : STATE_NAMES[selectedState]}
            </h2>
            <div className="prose prose-slate max-w-none">
              <p>
                BevRelay's Target List provides <strong>beverage startups</strong> with direct access to qualified buyers 
                in {viewMode === 'city' ? selectedCity : STATE_NAMES[selectedState]}. Whether you're looking to{' '}
                <strong>launch a spirit brand</strong>, distribute craft wine, or introduce a new RTD beverage, 
                our curated database connects you with the right decision-makers.
              </p>
              <p>
                Our <strong>beverage distribution network</strong> covers the top 50 US cities, including detailed 
                buyer contacts for on-premise (restaurants, bars, hotels) and off-premise (retail, liquor stores, 
                wine shops) accounts. <strong>Craft spirits</strong>, wine, beer, and functional beverage brands 
                use BevRelay to accelerate their market expansion.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {['beverage startup', 'launch spirit', 'craft spirits distribution', 'wine distribution', 'RTD beverage launch', 'beverage buyers'].map(keyword => (
                  <span key={keyword} className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* All 50 Cities List for SEO */}
          <div className="mt-8 bg-slate-100 rounded-2xl p-6">
            <h3 className="font-semibold text-navy mb-3">Beverage Buyer Database Coverage</h3>
            <p className="text-sm text-slate-600 mb-4">
              BevRelay's Target List covers the top 50 US cities for beverage distribution:
            </p>
            <div className="flex flex-wrap gap-2">
              {TOP_50_CITIES.map(city => (
                <button
                  key={city.city}
                  onClick={() => {
                    setViewMode('city');
                    setSelectedCity(city.city);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-xs px-2 py-1 bg-white rounded text-slate-600 hover:text-copper transition-colors"
                >
                  {city.city}, {city.state}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
